import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Form, Modal, Tooltip } from 'antd';
import ReCAPTCHA from 'react-google-recaptcha';

import FormInput from '../../inputWrapper/formInput';
import CustomButton from '../../../components/button';
import { INPUT_TYPES } from '../../../constants/componentTypes';

import { requiredField } from '../../../helpers/validationHelper';

import { useAuth } from '../../../routes/authContext';
import { useRegisterBibleSeminarMemberMutation } from '../../../redux/slices/bibleSeminarsApiSlice';
import { CAPTCHA_SITE_KEY } from '../../../constants/cookieConstants';

import { BRImages } from '../../../assets/imgs';
import dayjs from 'dayjs';

const RegisterSeminarModal = ({ isModalOpen, onCloseModal, activeBibleSeminar }) => {
    const [form] = Form.useForm();
    const { openNotification } = useAuth();
    const [isFormValid, setIsFormValid] = useState(false);
    const [isCaptchaVerified, setIsCaptchaVerified] = useState(false);
    const [registerMember, { isLoading }] = useRegisterBibleSeminarMemberMutation();

    const recaptchaRef = useRef(null);

    useEffect(() => {
        if (isModalOpen) {
            form.resetFields();
            setIsFormValid(false);
            setIsCaptchaVerified(false);
        }
    }, [isModalOpen, form]);

    const handleCaptchaChange = (value) => {
        setIsCaptchaVerified(!!value);
        form.validateFields().then(() => {
            const allFields = form.getFieldsValue();
            const hasError = form.getFieldsError().some((field) => field.errors.length > 0);

            const requiredFields = ['name', 'email', 'phoneNumber'];
            const hasEmptyRequired = requiredFields.some((reqName) => !allFields[reqName]);

            setIsFormValid(!hasError && !hasEmptyRequired && !!value);
        }).catch((error) => {
            console.error(error);
        });;
    };

    const handleNoLeadingSpaceChange = useCallback(
        (field) => {
            return (e) => {
                let { value } = e.target;
                if (value.startsWith(' ')) {
                    value = value.trimStart();
                }
                form.setFieldsValue({ [field]: value });
            };
        },
        [form]
    );

    const handleFieldsChange = (changedFields, allFields) => {
        const hasError = allFields.some((field) => (field.errors || []).length > 0);

        const requiredFields = ['name', 'email', 'phoneNumber'];
        const hasEmptyRequired = requiredFields.some((reqName) => {
            const fieldData = allFields.find((f) => f.name[0] === reqName);
            return !fieldData?.value;
        });

        setIsFormValid(!hasError && !hasEmptyRequired && isCaptchaVerified);
    };

    const onFinish = async (values) => {
        if (!isCaptchaVerified) {
            openNotification({
                message: 'Please verify that you are not a robot.',
                type: 'error',
                title: 'Error'
            });
            return;
        }

        const registrationData = {
            event_id: activeBibleSeminar.id,
            event_location: activeBibleSeminar?.location,
            name: values.name,
            email: values.email,
            contact_number: values.phoneNumber,
            address: values.address,
            how_did_you_hear: values.seminarSource || 'Friend',
            additional_info: values.additionalInfo
        };

        try {
            await registerMember(registrationData).unwrap();
            openNotification({
                message: 'Registration successful!',
                type: 'success',
                title: 'Success'
            });
            form.resetFields();
            onCloseModal();
        } catch (error) {
            console.error('Registration failed:', error);
            openNotification({
                message: 'Registration failed. Please try again.',
                type: 'error',
                title: 'Error'
            });
        }
    };

    const sourcesOptions = [
        { label: 'Online Ad', value: 'Online Ad' },
        { label: 'Newspaper Ad', value: 'Newspaper Ad' },
        { label: 'Print Flyer', value: 'Print Flyer' },
        { label: 'Friend', value: 'Friend' },
        { label: 'Other', value: 'Other' }
    ];

    const onResetClick = () => {
        form.resetFields();
        setIsFormValid(false);
        setIsCaptchaVerified(false);
        if (recaptchaRef.current) {
            recaptchaRef.current.reset();
        }
    };

    return (
        <Modal
            open={isModalOpen}
            title='Register - Bible Seminars'
            footer={
                <div className='flex justify-end w-100 gap-6'>
                    <CustomButton
                        title='Reset'
                        variant='outline'
                        className='radius-5'
                        onClick={onResetClick}
                        disabled={isLoading}
                    />
                    <CustomButton
                        title='Submit'
                        className='radius-5'
                        onClick={() => form.submit()}
                        disabled={!isFormValid || isLoading}
                    />
                </div>
            }
            onCancel={onCloseModal}
            className='register-seminar-modal common-modal-container common-modal-footer'
            closeIcon={<img src={BRImages.CommonImages.CloseGrayIcon} alt='close-icon' />}
            destroyOnClose
            centered
            maskClosable={false}
            width={800}
        >
            <Form
                layout='vertical'
                form={form}
                onFinish={onFinish}
                requiredMark={false}
                validateTrigger={['onChange', 'onBlur']}
                onFieldsChange={handleFieldsChange}
            >
                <Card className='user-card p-16 mb-24'>
                    <Tooltip title={activeBibleSeminar?.event_name}>
                        <p className='m-0 mb-10 manrope-16-bold text-ellipsis'>{activeBibleSeminar?.event_name}</p>
                    </Tooltip>
                    {[
                        { label: 'Time', content: activeBibleSeminar?.event_time },
                        {
                            label: 'Start Date',
                            content: activeBibleSeminar?.event_date
                                ? dayjs(activeBibleSeminar.event_date).format('DD/MM/YYYY')
                                : ''
                        },
                        { label: 'Location', content: activeBibleSeminar?.location }
                    ].map(({ label, content }, index) => (
                        <div className='flex justify-between mb-8 gap-8' key={index}>
                            <p className='m-0 manrope-16-medium card-label text-nowrap'>{label}:</p>
                            <Tooltip title={content}>
                                <p className='m-0 manrope-16-regular label-value text-ellipsis'>{content}</p>
                            </Tooltip>
                        </div>
                    ))}
                </Card>

                <div className='seminar-form-content gap-20'>
                    <FormInput
                        label='Name'
                        name='name'
                        placeholder='Enter your name'
                        rules={[requiredField(''), { max: 100, message: 'Name cannot exceed 100 characters' }]}
                        maxLength={100}
                        onChange={handleNoLeadingSpaceChange('name')}
                    />
                    <FormInput
                        label='Email'
                        name='email'
                        placeholder='Enter your email'
                        rules={[requiredField(''), { type: 'email', message: '' }]}
                        onChange={handleNoLeadingSpaceChange('email')}
                    />
                    <FormInput
                        label='Phone Number'
                        name='phoneNumber'
                        placeholder='Enter your phone number'
                        rules={[
                            requiredField(''),
                            {
                                pattern: /^\+?[0-9]+$/,
                                message: ''
                            },
                            { max: 15, message: '' },
                            { min: 8, message: '' }
                        ]}
                        maxLength={15}
                        onChange={handleNoLeadingSpaceChange('phoneNumber')}
                    />
                    <FormInput
                        label='Address'
                        name='address'
                        placeholder='Enter your address'
                        onChange={handleNoLeadingSpaceChange('address')}
                    />
                    <FormInput
                        label='How did you hear about the Seminars?'
                        name='seminarSource'
                        type={INPUT_TYPES.SELECT}
                        placeholder='Please select'
                        options={sourcesOptions}
                    />
                    <FormInput
                        label='Additional Info'
                        name='additionalInfo'
                        type={INPUT_TYPES.TEXT_AREA}
                        rows={1}
                        placeholder='Enter additional info'
                        onChange={handleNoLeadingSpaceChange('additionalInfo')}
                    />
                </div>

                <div className='seminar-info-container radius-6 mt-20 mb-20'>
                    <div className='flex items-center'>
                        <img src={BRImages.CommonImages.InfoBlueIcon} alt='info-icon' width={18} />
                        <p className='m-0 manrope-16-medium ml-8 text-primary-blue'>Free BibleSeminars</p>
                    </div>
                    <p className='m-0 manrope-16-regular info-note'>
                        There are absolutely no fees for the Seminars
                    </p>
                </div>

                <div className='seminar-info-container radius-6 mt-20 mb-20'>
                    <div className='flex items-center'>
                        <img src={BRImages.CommonImages.InfoBlueIcon} alt='info-icon' width={18} />
                        <p className='m-0 manrope-16-medium ml-8 text-primary-blue'>
                            Be sure to register early!
                        </p>
                    </div>
                    <p className='m-0 manrope-16-regular info-note'>
                        Registrations are accepted on a first come, first-served basis
                    </p>
                </div>

                <ReCAPTCHA ref={recaptchaRef} sitekey={CAPTCHA_SITE_KEY} onChange={handleCaptchaChange} />
            </Form>
        </Modal>
    );
};

export default RegisterSeminarModal;
